import { ChartView } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { sanitizeString } from '@e3dc-react/shell/Libs/strings'
import moment, { Moment } from '@e3dc-react/shell/Libs/moment'

interface ReturnType {
  getFormattedDateTimeForLegend: (view: ChartView, timeStamp: string | number | undefined, from: Moment | undefined) => string
}

/**
 * This hooks provide functions to get formatted date-time.
 */
export const useFormattedDateTimeForLegend = (): ReturnType => {
  const { t } = useCustomTranslation()
  /** these translations include empty characters which break datepicker and need to be sanitized */
  const dateFormat = sanitizeString(t('common.format.date'))
  const dateFormatWithoutDay = sanitizeString(t('common.format.dateWithoutDay'))

  /* get formatted date-time when user is not hovering over the System chart */
  const getFormattedDateTimeOnNotHover = (view: ChartView, date: Moment): string => {
    switch (view) {
      case 'year':
        return date.format('YYYY').toString()
      case 'month':
        return date.format(dateFormatWithoutDay).toString()
      case 'week':
        return `${date.format(dateFormat)} - ${date.clone().add(6, 'days').format(dateFormat)}`
      case 'day':
      default:
        return date.format(dateFormat).toString()
    }
  }

  /* get formatted date-time when user hover over the System chart */
  const getFormattedDateTimeOnHover = (view: ChartView, timeStamp: string | number): string => {
    switch (view) {
      case 'year':
        return moment(timeStamp).format(dateFormatWithoutDay).toString()
      case 'month':
        return moment(timeStamp).format(dateFormat).toString()
      case 'week':
        return moment(timeStamp).format(`${dateFormat} HH:mm`).toString()
      case 'day':
      default:
        return moment(timeStamp).format(`${dateFormat} HH:mm`).toString()
    }
  }

  /* get formatted date-time to display when user hover / not-hover over the chart */
  const getFormattedDateTimeForLegend = (view: ChartView, timeStamp: string | number | undefined, from: Moment | undefined): string => {
    /* timeStamp is available only when the user hover over the chart */
    if (timeStamp !== undefined) return getFormattedDateTimeOnHover(view, timeStamp)

    if (from) return getFormattedDateTimeOnNotHover(view, from)

    return '-'
  }

  return { getFormattedDateTimeForLegend }
}
